import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useClassName } from "@contember/react-utils";
import { useCallback, useMemo, useRef, memo, forwardRef } from "react";
import ReactSelect, { useStateManager } from "react-select";
import { useCommonReactSelectStyles } from "./useCommonReactSelectStyles.js";
import { useChangeValidationState } from "../Hooks/useNativeInput.js";
import { usePortalProvider } from "../../Portal/contexts.js";
import { noop } from "../../../utils/noop.js";
import { useInputClassName } from "../Hooks/useInputClassName.js";
function deriveSelectIndexValue(index) {
  return index === -1 ? "" : index.toString();
}
const SelectComponent = ({
  active,
  className: outerClassName,
  defaultValue: defaultValueProp,
  disabled,
  distinction,
  focused,
  hovered,
  id,
  intent,
  isSearchable = false,
  loading,
  name,
  notNull,
  onBlur,
  onChange,
  onFocus,
  onFocusChange,
  onValidationStateChange,
  options,
  placeholder,
  readOnly,
  required,
  rows,
  scheme,
  size,
  styles,
  validationState,
  value: valueProp,
  ...outerProps
}, forwardedRef) => {
  const className = useInputClassName({
    // ControlStateProps
    active,
    disabled,
    loading,
    readOnly,
    required,
    focused,
    hovered,
    // ControlDisplayProps
    className: outerClassName,
    distinction,
    intent,
    scheme,
    size,
    // ValidationStateProps
    validationState
  });
  const defaultStyles = useCommonReactSelectStyles({});
  const getOptionLabel = useCallback((option) => option.label, []);
  const getOptionValue = useCallback((option) => option.key, []);
  const isOptionDisabled = useCallback((option) => !!option.disabled, []);
  const optionsWithKeys = useMemo(() => options.map(
    (option, key) => ({ key: deriveSelectIndexValue(key), ...option })
  ), [options]);
  const defaultValue = useMemo(() => defaultValueProp !== void 0 ? optionsWithKeys.find((option) => option.value === defaultValueProp) : void 0, [defaultValueProp, optionsWithKeys]);
  const value = useMemo(() => valueProp !== void 0 ? optionsWithKeys.find((option) => option.value === valueProp) : void 0, [valueProp, optionsWithKeys]);
  const defaultInputValue = defaultValue?.label;
  const nativeValidationInput = useRef(null);
  useChangeValidationState({ ref: nativeValidationInput, onValidationStateChange });
  const reactSelectState = useStateManager({
    defaultInputValue,
    defaultValue,
    onChange: (newValue) => {
      const next = newValue?.value;
      if (next !== void 0 && valueProp !== next) {
        if (nativeValidationInput.current) {
          nativeValidationInput.current.value = optionsWithKeys.find((option) => option.value === next)?.key ?? "";
        }
        onChange?.(next);
      }
    },
    onBlur: () => {
      onBlur?.();
      onFocusChange?.(false);
    },
    onFocus: () => {
      onFocus?.();
      onFocusChange?.(true);
    },
    value
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ReactSelect,
      {
        isSearchable,
        className: useClassName("select", className),
        defaultValue,
        getOptionLabel,
        getOptionValue,
        id,
        isClearable: !required && !notNull,
        isDisabled: disabled || readOnly || loading,
        isLoading: loading,
        isOptionDisabled,
        menuPortalTarget: usePortalProvider(),
        name,
        options: optionsWithKeys,
        placeholder,
        ref: forwardedRef,
        ...reactSelectState,
        styles: {
          ...defaultStyles,
          ...styles
        }
      }
    ),
    /* @__PURE__ */ jsx(
      "input",
      {
        ref: nativeValidationInput,
        value: value?.key ?? "",
        onChange: noop,
        tabIndex: -1,
        required: required || notNull,
        style: nativeValidationInputStyle
      }
    )
  ] });
};
const nativeValidationInputStyle = {
  border: 0,
  clipPath: "inset(50%)",
  height: 0,
  padding: 0,
  pointerEvents: "none",
  position: "absolute",
  width: 0
};
const Select = memo(forwardRef(SelectComponent));
Select.displayName = "Interface.Select";
export {
  Select,
  SelectComponent
};
//# sourceMappingURL=Select.js.map
